<template>
    <BaseDialog
        :open="isOpen"
        :title="resolvedTitle"
        :return-focus-to="returnFocusTo"
        :disable-close-on-backdrop-click="loading"
        :show-close="!loading"
        @update:open="onOpenUpdate">
        <BaseNotification v-if="submitError" theme="error" hide-dismiss class="max-w-xs" :text="t('components.schedule.error')" />
        <ScheduleFollowUp
            :date="date"
            :recurring="recurring"
            :is-collapsed="isCollapsed"
            :disabled="loading"
            :coach-followup-events="coachFollowupEvents"
            @date-update="onDateUpdate"
            @recurring-update="onRecurringUpdate" />
        <template #footer>
            <BaseButton theme="tertiary" :disabled="loading" @click="dismiss">{{ dismissText ?? t("common.dismiss") }}</BaseButton>
            <BaseButton :disabled="loading" theme="primary" :aria-label="t('scheduleFollowup.actionLabel')" @click="schedule">
                <div v-if="loading" class="h-5 w-5 inline-block mx-auto">
                    <LoadingSpinner />
                </div>
                <template v-else>{{ t("scheduleFollowup.action") }}</template>
            </BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup>
import { nextWorkday, roundToNearest15 } from "/js/dateUtils.js";
import { router } from "@inertiajs/vue3";
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import ScheduleFollowUp from "~vue/ScheduleFollowUp.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { DateTime } from "luxon";
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const resolvedTitle = computed(() => title ?? t("components.schedule.defaultTitle"));

const { coachFollowupEvents, initialDate, eventDescription, customSchedule, analyticsSource, title } = defineProps({
    coachFollowupEvents: {
        // comes from coach_followup_events
        type: Array,
        default: () => [],
        required: false,
    },
    title: { type: String, default: undefined },
    dismissText: { type: String, default: undefined },
    initialDate: { type: Date, default: undefined },
    eventDescription: { type: String, default: undefined },
    customSchedule: { type: Function, default: undefined },
    analyticsSource: { type: String, default: undefined },
});

const { $sendEvent } = inject("globalProperties");
const userDetails = inject("user_details");
const DEFAULT_EVENT_DESCRIPTION = "Let's discuss what's coming up for you this week.";

const isOpen = ref(false);
const returnFocusTo = ref(null);
const date = ref(initialDate ?? nextWorkday(roundToNearest15(DateTime.now().plus({ days: 3 }))).toJSDate());
const loading = ref(false);
const submitError = ref(null);
const event = ref(null);
const recurring = ref(userDetails.value.prefers_weekly_check_in);
const isCollapsed = ref(false); // used to set initial collapsed state on ScheduleFollowup

const schedule = async () => {
    try {
        submitError.value = null;
        loading.value = true;
        if (customSchedule) {
            await customSchedule({ recurring: recurring.value, selectedDate: date.value });
        } else {
            await $sendEvent("schedule_follow_up", {
                event_description: eventDescription ?? event.value?.invite_description ?? DEFAULT_EVENT_DESCRIPTION,
                event_at_confirmed: DateTime.fromJSDate(date.value).toISO(),
                event_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                recurring: recurring.value,
                type: ChatAction.FOLLOW_UP,
                id: event.value ? event.value.id : 0,
            });
            if (analyticsSource) {
                logUserInteraction(AmplitudeEventNames.FOLLOWUP_SCHEDULED, { source: analyticsSource });
            }
            router.reload({ only: ["coach_followup_events"] });
        }
        dismiss();
    } catch (e) {
        submitError.value = e;
        logError();
    } finally {
        loading.value = false;
    }
};

const onDateUpdate = (newDate) => {
    date.value = newDate;
};
const onRecurringUpdate = (toggled) => {
    recurring.value = toggled;
};

const open = ({ eventData, showCalendar = false } = {}) => {
    if (eventData) {
        event.value = eventData;
        date.value = DateTime.fromISO(eventData.event_at_confirmed).toJSDate();
        recurring.value = eventData.recurring || false;
    }
    if (showCalendar) {
        isCollapsed.value = false;
    }

    returnFocusTo.value = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    isOpen.value = true;
};

const dismiss = () => {
    submitError.value = null;
    isOpen.value = false;
    const path = globalThis.location.pathname;
    globalThis.history.replaceState({}, "", path);
};

const onOpenUpdate = (value) => {
    if (value) {
        isOpen.value = true;
    } else {
        dismiss();
    }
};

defineExpose({ open, dismiss, recurring });
</script>
